import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaUserTie,
  FaSpinner,
} from "react-icons/fa";
import { getRouteResults, assignDriver } from "../../api/routeOptimization";
import { getAllUsers } from "../../api/user";
import "./RouteResults.css";

const PAGE_SIZE = 15;

const RouteResults = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "dateOptimized",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [showDriverMenu, setShowDriverMenu] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driverMenuLoading, setDriverMenuLoading] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [usersMap, setUsersMap] = useState({});

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await getRouteResults();
        console.log("API Response:", response); // Debug log

        // Handle different response structures
        let data = [];

        if (Array.isArray(response)) {
          // Direct array response
          data = response;
        } else if (response && typeof response === "object") {
          // Response is an object, check for array in various properties
          if (Array.isArray(response.message)) {
            data = response.message;
          } else if (typeof response.message === "string" && response[0]) {
            // If message is a string and response has numeric keys, it's a wrapped array
            data = Object.values(response).filter(
              (v) => v && typeof v === "object" && v.id,
            );
          } else {
            // Try other common properties
            const possibleData =
              response.data || response.routes || response.results;
            data = Array.isArray(possibleData) ? possibleData : [];
          }
        }

        console.log("Extracted data:", data); // Debug log
        setRoutes(data);

        // Fetch users for user map
        const usersResponse = await getAllUsers();
        const usersData = Array.isArray(usersResponse) ? usersResponse : [];
        const userMap = {};
        usersData.forEach((user) => {
          userMap[user.username] = user;
        });
        setUsersMap(userMap);
      } catch (err) {
        console.error("Error fetching route results:", err);
        setError("Failed to load route results.");
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, []);

  // Fetch routes function for reuse
  const performFetchRoutes = async () => {
    setLoading(true);
    try {
      const response = await getRouteResults();
      console.log("API Response:", response); // Debug log

      // Handle different response structures
      let data = [];

      if (Array.isArray(response)) {
        // Direct array response
        data = response;
      } else if (response && typeof response === "object") {
        // Response is an object, check for array in various properties
        if (Array.isArray(response.message)) {
          data = response.message;
        } else if (typeof response.message === "string" && response[0]) {
          // If message is a string and response has numeric keys, it's a wrapped array
          data = Object.values(response).filter(
            (v) => v && typeof v === "object" && v.id,
          );
        } else {
          // Try other common properties
          const possibleData =
            response.data || response.routes || response.results;
          data = Array.isArray(possibleData) ? possibleData : [];
        }
      }

      console.log("Extracted data:", data); // Debug log
      setRoutes(data);

      // Fetch users for user map
      const usersResponse = await getAllUsers();
      const usersData = Array.isArray(usersResponse) ? usersResponse : [];
      const userMap = {};
      usersData.forEach((user) => {
        userMap[user.username] = user;
      });
      setUsersMap(userMap);

      setError(null);
    } catch (err) {
      console.error("Error fetching route results:", err);
      setError("Failed to load route results.");
    } finally {
      setLoading(false);
    }
  };

  // Parse optimization response for backward compatibility (legacy format)
  const parseLegacyOptimizationData = (route) => {
    // If optimizationResponse exists, parse it for legacy format
    if (route.optimizationResponse) {
      try {
        const data = JSON.parse(route.optimizationResponse);
        const routeData = data.routes?.[0];
        const summary = data.summary;

        return {
          steps: routeData?.steps || [],
          vehicleDescription:
            routeData?.description || route.vehicleDescription,
          totalCost: summary?.cost || 0,
          totalDuration: summary?.duration || 0,
        };
      } catch (err) {
        console.error("Error parsing legacy optimization response:", err);
      }
    }
    return null;
  };

  // Fetch drivers for assignment menu
  const fetchDriversForMenu = async () => {
    setDriverMenuLoading(true);
    try {
      const usersResponse = await getAllUsers();
      const usersData = Array.isArray(usersResponse) ? usersResponse : [];
      const driversWithRole4 = usersData.filter(
        (user) => user.roleId === 4 && user.isActive === true,
      );
      setDrivers(driversWithRole4);
    } catch (err) {
      console.error("Error fetching drivers:", err);
      setError("Failed to load drivers");
    } finally {
      setDriverMenuLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filtered list by search term
  const filteredRoutes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return routes;
    return routes.filter((route) => {
      return (
        String(route.no || "")
          .toLowerCase()
          .includes(term) ||
        String(route.vehicleId || "")
          .toLowerCase()
          .includes(term) ||
        String(route.vehicleDescription || "")
          .toLowerCase()
          .includes(term) ||
        String(route.companyCode || "")
          .toLowerCase()
          .includes(term) ||
        String(route.optimizationBatchId || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [routes, searchTerm]);

  // Sorting
  const sortedRoutes = useMemo(() => {
    const list = [...filteredRoutes];
    const { key, direction } = sortConfig || {};
    if (!key) return list;

    list.sort((a, b) => {
      let A, B;

      if (key === "deliveryCount") {
        A = a.orders?.length || 0;
        B = b.orders?.length || 0;
      } else {
        A = a[key];
        B = b[key];
      }

      if (typeof A === "string") A = A.toLowerCase();
      if (typeof B === "string") B = B.toLowerCase();
      if (A === B) return 0;
      const cmp = A > B ? 1 : -1;
      return direction === "asc" ? cmp : -1 * cmp;
    });
    return list;
  }, [filteredRoutes, sortConfig]);

  // Pagination
  const pageCount = Math.max(1, Math.ceil(sortedRoutes.length / PAGE_SIZE));
  const pagedRoutes = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRoutes.slice(start, start + PAGE_SIZE);
  }, [sortedRoutes, currentPage]);

  // Reset to first page when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
    setPageInput("1");
  }, [searchTerm, sortConfig]);

  // Keep page input synced with current page
  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const num = parseInt(pageInput, 10);
    if (num >= 1 && num <= pageCount) {
      setCurrentPage(num);
    } else {
      setPageInput(String(currentPage));
    }
  };

  const handleViewDetails = (route) => {
    // Navigate to RouteDetails page, passing the route as state
    navigate(`/route-details/${route.id}`, { state: { route } });
  };

  const handleOpenDriverMenu = (route) => {
    setSelectedRoute(route);
    setShowDriverMenu(true);
    fetchDriversForMenu();
  };

  const handleCloseDriverMenu = () => {
    setShowDriverMenu(false);
    setSelectedRoute(null);
  };

  const handleAssignDriver = async (driver) => {
    if (!selectedRoute) return;
    setAssignmentLoading(true);
    try {
      const response = await assignDriver(
        driver.username,
        selectedRoute.optimizationBatchId,
        selectedRoute.vehicleId,
      );
      if (response?.success !== false) {
        setError(null);
        setShowDriverMenu(false);
        setSelectedRoute(null);
        // Refresh the routes
        await performFetchRoutes();
      } else {
        setError(response?.message || "Failed to assign driver");
      }
    } catch (err) {
      console.error("Assignment error:", err);
      setError("Failed to assign driver");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <FaArrowUp style={{ marginLeft: "4px", fontSize: "0.7rem" }} />
    ) : (
      <FaArrowDown style={{ marginLeft: "4px", fontSize: "0.7rem" }} />
    );
  };

  if (loading) {
    return (
      <div className="route-results-container">
        <div className="route-results-loading">
          <div className="route-spinner"></div>
          <p>Loading route results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="route-results-container">
        <div className="route-results-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="route-results-container">
      <div className="route-results-page-header">
        <h1 className="route-results-title">Route Optimization Results</h1>
        <p className="route-results-page-subtitle">
          View and analyze optimized delivery routes
        </p>
      </div>

      {/* Search Bar */}
      <div className="route-results-controls">
        <input
          type="text"
          placeholder="Search by order no, vehicle, batch ID, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="route-results-search"
        />
      </div>

      {/* Table */}
      {filteredRoutes.length === 0 ? (
        <div className="route-results-empty">No route results found.</div>
      ) : (
        <>
          <div className="route-results-table-wrapper">
            <table className="route-results-table">
              <thead>
                <tr>
                  {/* <th
                    className="route-results-glass-header"
                    onClick={() => handleSort("no")}
                    style={{ cursor: "pointer" }}
                  >
                    Order No {renderSortIcon("no")}
                  </th> */}
                  <th
                    className="route-results-glass-header"
                    onClick={() => handleSort("vehicleId")}
                    style={{ cursor: "pointer" }}
                  >
                    Vehicle ID {renderSortIcon("vehicleId")}
                  </th>
                  <th className="route-results-glass-header">Vehicle</th>
                  <th
                    className="route-results-glass-header"
                    onClick={() => handleSort("optimizationBatchId")}
                    style={{ cursor: "pointer" }}
                  >
                    Batch ID {renderSortIcon("optimizationBatchId")}
                  </th>
                  <th
                    className="route-results-glass-header"
                    onClick={() => handleSort("dateOptimized")}
                    style={{ cursor: "pointer" }}
                  >
                    Date Optimized {renderSortIcon("dateOptimized")}
                  </th>
                  <th
                    className="route-results-glass-header"
                    onClick={() => handleSort("deliveryCount")}
                    style={{ cursor: "pointer" }}
                  >
                    Orders {renderSortIcon("deliveryCount")}
                  </th>
                  <th className="route-results-glass-header">Total Amount</th>
                  <th className="route-results-glass-header">Assigned User</th>
                  <th className="route-results-glass-header">Status</th>
                  <th className="route-results-glass-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRoutes.map((route) => {
                  const assignedUser = route.userId
                    ? usersMap[route.userId]
                    : null;
                  return (
                    <tr key={route.id} className="route-results-glass-row">
                      {/* <td>{route.no}</td> */}
                      <td>{route.vehicleId}</td>
                      <td className="route-results-vehicle-desc">
                        {route.vehicleDescription || "N/A"}
                      </td>
                      <td>
                        <span className="route-results-batch-badge">
                          {route.optimizationBatchId || "N/A"}
                        </span>
                      </td>
                      <td>{formatDate(route.dateOptimized)}</td>
                      <td>
                        <span className="route-results-delivery-badge">
                          {route.orders?.length || 0}
                        </span>
                      </td>
                      <td>
                        <span className="route-results-amount-value">
                          $
                          {route.orders
                            ?.reduce(
                              (sum, order) => sum + (order.totalAmount || 0),
                              0,
                            )
                            .toFixed(2) || "0.00"}
                        </span>
                      </td>
                      <td>
                        {assignedUser ? (
                          <span className="route-results-user-badge">
                            {assignedUser.name}
                          </span>
                        ) : route.userId ? (
                          <span className="route-results-user-badge">
                            {route.userId}
                          </span>
                        ) : (
                          <span className="route-results-unassigned">
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`route-results-status-badge ${
                            route.isDelivered ? "delivered" : "pending"
                          }`}
                        >
                          {route.isDelivered ? "✓ Delivered" : "⏱ Pending"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="route-results-action-btn"
                          onClick={() => handleViewDetails(route)}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="route-results-action-btn route-results-assign-btn"
                          onClick={() => handleOpenDriverMenu(route)}
                          title="Assign Driver"
                        >
                          <FaUserTie />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="route-results-pagination">
            <span className="route-results-pagination-info">
              Showing {pagedRoutes.length} of {sortedRoutes.length} results
            </span>
            <div className="route-results-pagination-controls">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="route-results-pagination-btn"
              >
                Previous
              </button>
              <form
                onSubmit={handlePageInputSubmit}
                className="route-results-pagination-form"
              >
                <span>Page</span>
                <input
                  type="number"
                  min="1"
                  max={pageCount}
                  value={pageInput}
                  onChange={handlePageInputChange}
                  className="route-results-pagination-input"
                />
                <span>of {pageCount}</span>
              </form>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(pageCount, prev + 1))
                }
                disabled={currentPage === pageCount}
                className="route-results-pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Driver Assignment Menu */}
      {showDriverMenu && selectedRoute && (
        <div
          className="route-results-modal-overlay"
          onClick={handleCloseDriverMenu}
        >
          <div
            className="route-results-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="route-results-modal-header">
              <h3>Assign Driver to {selectedRoute.no}</h3>
              <button
                className="route-results-modal-close"
                onClick={handleCloseDriverMenu}
              >
                ×
              </button>
            </div>
            <div className="route-results-modal-body">
              {driverMenuLoading ? (
                <div className="route-results-loading-drivers">
                  <FaSpinner className="fa-spinner" />
                  <p>Loading drivers...</p>
                </div>
              ) : drivers.length > 0 ? (
                <div className="route-results-drivers-list">
                  {drivers.map((driver) => (
                    <button
                      key={driver.username}
                      className="route-results-driver-item"
                      onClick={() => handleAssignDriver(driver)}
                      disabled={assignmentLoading}
                    >
                      <span className="driver-name">{driver.name}</span>
                      <span className="driver-username">
                        ({driver.username})
                      </span>
                      {assignmentLoading && (
                        <FaSpinner className="fa-spinner-small" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="route-results-no-drivers">No drivers available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteResults;
